import React, { useState } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, Plus, Trash2, Upload, Loader2 } from "lucide-react";

const CATEGORIES = ["pilgrimage", "himalayan", "leisure", "heritage", "offbeat", "beach"];
const TOUR_TYPES = ["domestic", "private", "group", "international"];

const empty = {
  title: "", slug: "", summary: "", description: "", region: "",
  destinations: [], category: "leisure", tour_type: "domestic",
  nights: 0, days: 0, price_from: 0, currency: "INR", season: "",
  tags: [], hero_image: "", gallery: [], inclusions: [], exclusions: [],
  day_plan: [], map_points: [], popularity: 0, show_popularity: false, featured: false, published: true,
};

export function ItineraryEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { ...empty, ...initial } : { ...empty });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = Boolean(initial);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, str) => set(k, str.split(",").map((s) => s.trim()).filter(Boolean));

  const upload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = mediaUrl(data.url);
      if (target === "hero") set("hero_image", url);
      else set("gallery", [...form.gallery, url]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const addDay = () => set("day_plan", [...form.day_plan, { day: form.day_plan.length + 1, title: "", detail: "" }]);
  const updDay = (i, k, v) => set("day_plan", form.day_plan.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
  const delDay = (i) => set("day_plan", form.day_plan.filter((_, idx) => idx !== i).map((d, idx) => ({ ...d, day: idx + 1 })));

  const mp = form.map_points || [];
  const addPoint = () => set("map_points", [...mp, { day: mp.length + 1, name: "", lat: 0, lng: 0 }]);
  const updPoint = (i, k, v) => set("map_points", mp.map((p, idx) => (idx === i ? { ...p, [k]: k === "name" ? v : Number(v) } : p)));
  const delPoint = (i) => set("map_points", mp.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setBusy(true);
    const payload = { ...form, nights: Number(form.nights) || 0, days: Number(form.days) || 0, price_from: Number(form.price_from) || 0 };
    try {
      if (isEdit) await api.put(`/itineraries/${initial.slug}`, payload);
      else await api.post("/itineraries", payload);
      toast.success(isEdit ? "Itinerary updated" : "Itinerary created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-md border border-[#553204]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#F49E1A]";
  const label = "text-xs font-semibold text-[#553204] mb-1 block";

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="itinerary-editor">
      <div className="bg-[#FFF7EC] rounded-md w-full max-w-3xl my-8">
        <div className="sticky top-0 bg-[#FFF7EC] border-b border-[#553204]/10 px-6 py-4 flex items-center justify-between rounded-t-md z-10">
          <h2 className="font-display text-2xl text-[#2A1808]">{isEdit ? "Edit journey" : "New journey"}</h2>
          <button onClick={onClose} data-testid="editor-close"><X className="text-[#8A6B47]" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={label}>Title *</label><input data-testid="editor-title" className={input} value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><label className={label}>Region</label><input className={input} value={form.region} onChange={(e) => set("region", e.target.value)} placeholder="e.g. Jammu & Kashmir" /></div>
          </div>
          <div><label className={label}>Summary (one editorial line)</label><input className={input} value={form.summary} onChange={(e) => set("summary", e.target.value)} /></div>
          <div><label className={label}>Description</label><textarea className={input} rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><label className={label}>Category</label><select className={input} value={form.category} onChange={(e) => set("category", e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className={label}>Tour type</label><select className={input} value={form.tour_type} onChange={(e) => set("tour_type", e.target.value)}>{TOUR_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className={label}>Nights</label><input type="number" className={input} value={form.nights} onChange={(e) => set("nights", e.target.value)} /></div>
            <div><label className={label}>Days</label><input type="number" className={input} value={form.days} onChange={(e) => set("days", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div><label className={label}>Price from (₹)</label><input type="number" data-testid="editor-price" className={input} value={form.price_from} onChange={(e) => set("price_from", e.target.value)} /></div>
            <div><label className={label}>Season</label><input className={input} value={form.season} onChange={(e) => set("season", e.target.value)} placeholder="e.g. May–October" /></div>
            <div><label className={label}>Destinations (comma sep)</label><input className={input} defaultValue={form.destinations.join(", ")} onBlur={(e) => setList("destinations", e.target.value)} /></div>
          </div>
          <div><label className={label}>Tags (comma sep)</label><input className={input} defaultValue={form.tags.join(", ")} onBlur={(e) => setList("tags", e.target.value)} placeholder="honeymoon, family, adventure…" /></div>

          {/* Images */}
          <div>
            <label className={label}>Hero image</label>
            <div className="flex items-center gap-3">
              <input className={input} value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} placeholder="Paste URL or upload →" />
              <label className="shrink-0 cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-[#553204]/20 px-3 py-2 text-xs text-[#553204] bg-white">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "hero")} data-testid="editor-hero-upload" />
              </label>
            </div>
            {form.hero_image && <img src={form.hero_image} alt="" className="mt-2 h-24 rounded object-cover" />}
          </div>
          <div>
            <label className={label}>Gallery</label>
            <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-[#553204]/20 px-3 py-2 text-xs text-[#553204] bg-white">
              <Upload size={14} /> Add image
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "gallery")} />
            </label>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.gallery.map((g, i) => (
                <div key={i} className="relative">
                  <img src={g} alt="" className="h-16 w-24 rounded object-cover" />
                  <button onClick={() => set("gallery", form.gallery.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X size={12} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={label}>Inclusions (comma sep)</label><textarea className={input} rows={3} defaultValue={form.inclusions.join(", ")} onBlur={(e) => setList("inclusions", e.target.value)} /></div>
            <div><label className={label}>Exclusions (comma sep)</label><textarea className={input} rows={3} defaultValue={form.exclusions.join(", ")} onBlur={(e) => setList("exclusions", e.target.value)} /></div>
          </div>

          {/* Day plan */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={label}>Day-by-day plan</label>
              <button onClick={addDay} className="inline-flex items-center gap-1 text-xs text-[#F49E1A] font-semibold" data-testid="editor-add-day"><Plus size={14} /> Add day</button>
            </div>
            <div className="space-y-2">
              {form.day_plan.map((d, i) => (
                <div key={i} className="flex gap-2 items-start bg-white rounded-md border border-[#553204]/10 p-2">
                  <span className="mt-2 text-xs font-bold text-[#F49E1A] w-8 text-center">D{d.day}</span>
                  <div className="flex-1 space-y-1.5">
                    <input className={input} placeholder="Title" value={d.title} onChange={(e) => updDay(i, "title", e.target.value)} />
                    <textarea className={input} rows={2} placeholder="Detail" value={d.detail} onChange={(e) => updDay(i, "detail", e.target.value)} />
                  </div>
                  <button onClick={() => delDay(i)} className="mt-2 text-red-400"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Map points */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={label}>Map locations (drawn on the tour map)</label>
              <button onClick={addPoint} className="inline-flex items-center gap-1 text-xs text-[#F49E1A] font-semibold" data-testid="editor-add-point"><Plus size={14} /> Add location</button>
            </div>
            <div className="space-y-2">
              {mp.map((p, i) => (
                <div key={i} className="flex gap-2 items-center bg-white rounded-md border border-[#553204]/10 p-2">
                  <input type="number" className={`${input} w-14`} value={p.day || ""} onChange={(e) => updPoint(i, "day", e.target.value)} placeholder="Day" />
                  <input className={`${input} flex-1`} value={p.name} onChange={(e) => updPoint(i, "name", e.target.value)} placeholder="Place name" />
                  <input type="number" step="0.0001" className={`${input} w-24`} value={p.lat} onChange={(e) => updPoint(i, "lat", e.target.value)} placeholder="Lat" />
                  <input type="number" step="0.0001" className={`${input} w-24`} value={p.lng} onChange={(e) => updPoint(i, "lng", e.target.value)} placeholder="Lng" />
                  <button onClick={() => delPoint(i)} className="text-red-400"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" data-testid="editor-published" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Published</label>
            <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" data-testid="editor-featured" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage</label>
            <label className="flex items-center gap-2 text-sm text-[#553204]"><input type="checkbox" data-testid="editor-show-popularity" checked={form.show_popularity} onChange={(e) => set("show_popularity", e.target.checked)} /> Show popularity</label>
            <label className="flex items-center gap-2 text-sm text-[#553204]">Popularity <input type="number" step="0.1" min="0" max="10" data-testid="editor-popularity" className={`${input} w-20`} value={form.popularity} onChange={(e) => set("popularity", Number(e.target.value))} /></label>
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#FFF7EC] border-t border-[#553204]/10 px-6 py-4 flex justify-end gap-3 rounded-b-md">
          <button onClick={onClose} className="rounded-full border border-[#553204]/20 px-5 py-2.5 text-sm text-[#553204]">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="editor-save" className="rounded-full btn-orange px-6 py-2.5 text-sm disabled:opacity-60">{busy ? "Saving…" : "Save journey"}</button>
        </div>
      </div>
    </div>
  );
}
